import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ImageIcon, Grid, List, Filter, Search, SortAsc, Eye, Heart, Download, Share2, User, Calendar, ChevronDown } from 'lucide-react';
import AccessLevelBadge from '../components/AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import PageHeader from '../components/PageHeader';
import ShareModal from '../components/ShareModal';
import DownloadPaywall from '../components/DownloadPaywall';
import SignupInviteModal from '../components/SignupInviteModal';

const Gallery = () => {
    const { isAuthenticated, hasActiveSubscription } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [sortBy, setSortBy] = useState('recent'); // 'recent', 'popular', 'title', 'artist'
    const [filterCategory, setFilterCategory] = useState('all');
    const [filterMedium, setFilterMedium] = useState('all');
    const [filterUser, setFilterUser] = useState('all'); // 'all' or username
    const [searchTerm, setSearchTerm] = useState('');
    const [shareModal, setShareModal] = useState({ isOpen: false, image: null });
    const [downloadPaywall, setDownloadPaywall] = useState({ isOpen: false, image: null });
    const [users, setUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'image' });
    const [showFilters, setShowFilters] = useState(false);

    const categories = [
        'all', 'Photography', 'Digital Art', 'Painting', 'Drawing', 'Mixed Media', 
        'Abstract', 'Portrait', 'Landscape', 'Street Art', 'Illustration', 'Other'
    ];

    const mediums = [
        'all', 'Digital', 'Oil Paint', 'Acrylic', 'Watercolor', 'Pencil', 'Charcoal',
        'Ink', 'Mixed Media', 'Photography', 'Pastel', 'Marker', 'Other'
    ];

    const sortOptions = [
        { value: 'recent', label: 'Recently Added' },
        { value: 'popular', label: 'Most Popular' },
        { value: 'title', label: 'Title A-Z' },
        { value: 'artist', label: 'Artist A-Z' }
    ];


    useEffect(() => {
        fetchImages();
        fetchUsers();
    }, []);

    useEffect(() => {
        fetchImages();
    }, [sortBy, filterCategory, filterMedium, filterUser, searchTerm]);

    const fetchImages = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await api.get('/upload/public/image?limit=50');
            
            if (response.data.success) {
                let filteredImages = response.data.data;

                // Apply category filter
                if (filterCategory !== 'all') {
                    filteredImages = filteredImages.filter(img => 
                        img.category === filterCategory
                    );
                }

                // Apply medium filter
                if (filterMedium !== 'all') {
                    filteredImages = filteredImages.filter(img => 
                        img.medium === filterMedium
                    );
                }

                // Apply user filter
                if (filterUser !== 'all') {
                    filteredImages = filteredImages.filter(img => 
                        img.username === filterUser || 
                        (img.display_name && img.display_name.toLowerCase() === filterUser.toLowerCase())
                    );
                }

                // Apply search filter
                if (searchTerm) {
                    filteredImages = filteredImages.filter(img =>
                        (img.title && img.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        (img.artist && img.artist.toLowerCase().includes(searchTerm.toLowerCase())) ||
                        (img.description && img.description.toLowerCase().includes(searchTerm.toLowerCase()))
                    );
                }

                // Apply sorting
                filteredImages.sort((a, b) => {
                    switch (sortBy) {
                        case 'popular':
                            return (b.play_count || 0) - (a.play_count || 0);
                        case 'title':
                            return (a.title || '').localeCompare(b.title || '');
                        case 'artist':
                            return (a.artist || a.display_name || '').localeCompare(b.artist || b.display_name || '');
                        case 'recent':
                        default:
                            return new Date(b.created_at) - new Date(a.created_at);
                    }
                });

                setImages(filteredImages);
            } else {
                setError('Failed to load images');
            }
        } catch (err) {
            console.error('Error fetching images:', err);
            setError('Unable to load gallery. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users/public');
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const handleShareClick = (image) => {
        setShareModal({ isOpen: true, image });
    };

    const handleDownloadClick = (image) => {
        setDownloadPaywall({ isOpen: true, image });
    };

    const handleLike = async (image) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: 'image' });
            return;
        }

        try {
            const response = await api.post(`/upload/${image.id}/like`);
            
            if (response.data.success) {
                // Update the image in the state to reflect the new like status
                setImages(prevImages => 
                    prevImages.map(img => 
                        img.id === image.id 
                            ? { 
                                ...img, 
                                user_has_liked: response.data.data.liked,
                                like_count: response.data.data.total_likes 
                            }
                            : img
                    )
                );
            }
        } catch (error) {
            console.error('Error toggling like:', error);
        }
    };

    const formatTimeAgo = (dateString) => {
        const now = new Date();
        const then = new Date(dateString);
        const diffInHours = Math.floor((now - then) / (1000 * 60 * 60));
        
        if (diffInHours < 1) return 'Just now';
        if (diffInHours < 24) return `${diffInHours}h ago`;
        
        const diffInDays = Math.floor(diffInHours / 24);
        if (diffInDays < 7) return `${diffInDays}d ago`;
        
        const diffInWeeks = Math.floor(diffInDays / 7);
        if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
        
        const diffInMonths = Math.floor(diffInDays / 30);
        return `${diffInMonths}mo ago`;
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '';
        const mb = (bytes / (1024 * 1024)).toFixed(1);
        return `${mb} MB`;
    };

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader 
                title="Artwork and Photos Gallery"
                subtitle="Discover amazing artwork and photos from The Deep Wave community"
                icon={<ImageIcon className="w-8 h-8" />}
            />


            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8 pb-8 sm:pb-12">
                {/* Filters and Controls */}
                <div className="mb-6 sm:mb-8 space-y-4 lg:space-y-0">
                    {/* Search Bar */}
                    <div className="relative">
                        <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search artwork, artists, or descriptions..."
                            className="w-full pl-10 pr-4 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* Filters and View Controls */}

                    {/* Mobile: toggle bar — always visible */}
                    <div className="flex items-center gap-2 lg:hidden">
                        <button
                            onClick={() => setShowFilters(f => !f)}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-white text-sm"
                        >
                            <Filter className="w-4 h-4" />
                            Filters
                            {(filterCategory !== 'all' || filterMedium !== 'all' || filterUser !== 'all' || sortBy !== 'recent') && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full" />
                            )}
                            <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showFilters ? 'rotate-180' : ''}`} />
                        </button>
                        <div className="flex rounded-lg border border-gray-700 overflow-hidden ml-auto">
                            <button
                                onClick={() => setViewMode('grid')}
                                className={`px-3 py-2 flex items-center gap-1 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                            >
                                <Grid className="w-4 h-4" />
                                Grid
                            </button>
                            <button
                                onClick={() => setViewMode('list')}
                                className={`px-3 py-2 flex items-center gap-1 text-sm border-l border-gray-700 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                            >
                                <List className="w-4 h-4" />
                                List
                            </button>
                        </div>
                        <span className="text-sm text-gray-400 whitespace-nowrap">
                            {loading ? '...' : `${images.length}`}
                        </span>
                    </div>

                    {/* Mobile: collapsible filter panel */}
                    {showFilters && (
                        <div className="lg:hidden grid grid-cols-1 gap-3 pt-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                                <select
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                    value={filterCategory}
                                    onChange={(e) => setFilterCategory(e.target.value)}
                                >
                                    {categories.map(category => (
                                        <option key={category} value={category}>
                                            {category === 'all' ? 'All Categories' : category}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Medium</label>
                                <select
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                    value={filterMedium}
                                    onChange={(e) => setFilterMedium(e.target.value)}
                                >
                                    {mediums.map(medium => (
                                        <option key={medium} value={medium}>
                                            {medium === 'all' ? 'All Mediums' : medium}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="relative">
                                <label className="block text-sm font-medium text-gray-300 mb-1">Browse by Artist</label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        placeholder={filterUser === 'all' ? 'Search artists...' : filterUser}
                                        value={userSearchTerm}
                                        onChange={(e) => setUserSearchTerm(e.target.value)}
                                        onFocus={() => setShowUserDropdown(true)}
                                        onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                    />
                                    <User className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                                    {showUserDropdown && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto z-50">
                                            <button
                                                onClick={() => { setFilterUser('all'); setUserSearchTerm(''); setShowUserDropdown(false); }}
                                                className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                                            >
                                                All Artists
                                            </button>
                                            {users
                                                .filter(user => !userSearchTerm ||
                                                    (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                                    (user.display_name && user.display_name.toLowerCase().includes(userSearchTerm.toLowerCase()))
                                                )
                                                .map(user => (
                                                    <button
                                                        key={user.id}
                                                        onClick={() => { navigate(`/gallery/artist/${user.username}`); setUserSearchTerm(''); setShowUserDropdown(false); }}
                                                        className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                    >
                                                        <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                                            {user.profilePictureUrl ? (
                                                                <img src={user.profilePictureUrl} alt={user.display_name || user.username} width="24" height="24" className="w-full h-full object-cover" />
                                                            ) : (
                                                                <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                                                                    <span className="text-white text-xs">{(user.display_name || user.username)?.charAt(0)}</span>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div>
                                                            <div className="font-medium">{user.display_name || user.username}</div>
                                                            {user.display_name && <div className="text-gray-400 text-xs">@{user.username}</div>}
                                                        </div>
                                                    </button>
                                                ))
                                            }
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-300 mb-1">Sort By</label>
                                <select
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                >
                                    {sortOptions.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    )}

                    {/* Desktop: full filter grid */}
                    <div className="hidden lg:grid grid-cols-5 gap-4 items-center">
                        {/* Category Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Category</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                value={filterCategory}
                                onChange={(e) => setFilterCategory(e.target.value)}
                            >
                                {categories.map(category => (
                                    <option key={category} value={category}>
                                        {category === 'all' ? 'All Categories' : category}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Medium Filter */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Medium</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                value={filterMedium}
                                onChange={(e) => setFilterMedium(e.target.value)}
                            >
                                {mediums.map(medium => (
                                    <option key={medium} value={medium}>
                                        {medium === 'all' ? 'All Mediums' : medium}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* User Filter */}
                        <div className="relative">
                            <label className="block text-sm font-medium text-gray-300 mb-1">Browse by Artist</label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder={filterUser === 'all' ? 'Search artists...' : filterUser}
                                    value={userSearchTerm}
                                    onChange={(e) => setUserSearchTerm(e.target.value)}
                                    onFocus={() => setShowUserDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:border-purple-500 focus:ring-1 focus:ring-purple-500 transition-colors"
                                />
                                <User className="absolute right-3 top-2.5 h-4 w-4 text-gray-400" />
                                {showUserDropdown && (
                                    <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-lg max-h-40 overflow-y-auto z-50">
                                        <button
                                            onClick={() => { setFilterUser('all'); setUserSearchTerm(''); setShowUserDropdown(false); }}
                                            className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-gray-700 transition-colors"
                                        >
                                            All Artists
                                        </button>
                                        {users
                                            .filter(user => !userSearchTerm ||
                                                (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                                (user.display_name && user.display_name.toLowerCase().includes(userSearchTerm.toLowerCase()))
                                            )
                                            .map(user => (
                                                <button
                                                    key={user.id}
                                                    onClick={() => { navigate(`/gallery/artist/${user.username}`); setUserSearchTerm(''); setShowUserDropdown(false); }}
                                                    className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                >
                                                    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                                        {user.profilePictureUrl ? (
                                                            <img src={user.profilePictureUrl} alt={user.display_name || user.username} width="24" height="24" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                                                                <span className="text-white text-xs">{(user.display_name || user.username)?.charAt(0)}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{user.display_name || user.username}</div>
                                                        {user.display_name && <div className="text-gray-400 text-xs">@{user.username}</div>}
                                                    </div>
                                                </button>
                                            ))
                                        }
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Sort */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">Sort By</label>
                            <select
                                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                {sortOptions.map(option => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* View Mode Toggle */}
                        <div>
                            <label className="block text-sm font-medium text-gray-300 mb-1">View</label>
                            <div className="flex rounded-lg border border-gray-700 overflow-hidden">
                                <button
                                    onClick={() => setViewMode('grid')}
                                    className={`px-3 py-2 flex items-center gap-1 text-sm ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                                >
                                    <Grid className="w-4 h-4" />
                                    Grid
                                </button>
                                <button
                                    onClick={() => setViewMode('list')}
                                    className={`px-3 py-2 flex items-center gap-1 text-sm border-l border-gray-700 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-300 hover:bg-gray-700'}`}
                                >
                                    <List className="w-4 h-4" />
                                    List
                                </button>
                            </div>
                        </div>

                        {/* Results Count */}
                        <div className="text-sm text-gray-400 lg:text-right col-span-5">
                            {loading ? 'Loading...' : `${images.length} artwork${images.length !== 1 ? 's' : ''}`}
                        </div>
                    </div>
                </div>

                {/* Loading State */}
                {loading && (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[...Array(8)].map((_, index) => (
                            <div key={index} className="glass-card overflow-hidden animate-pulse">
                                <div className="aspect-square bg-gray-700"></div>
                                <div className="p-4">
                                    <div className="h-4 bg-gray-700 rounded w-3/4 mb-2"></div>
                                    <div className="h-3 bg-gray-700 rounded w-1/2"></div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Error State */}
                {error && (
                    <div className="glass-card p-8 text-center">
                        <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-300 mb-2">Unable to Load Gallery</h3>
                        <p className="text-gray-400 mb-4">{error}</p>
                        <button 
                            onClick={fetchImages}
                            className="btn-primary"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {/* No Results */}
                {!loading && !error && images.length === 0 && (
                    <div className="glass-card p-8 text-center">
                        <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                        <h3 className="text-xl font-semibold text-gray-300 mb-2">No Artwork Found</h3>
                        <p className="text-gray-400">
                            {searchTerm || filterCategory !== 'all' || filterMedium !== 'all'
                                ? 'Try adjusting your search or filters.'
                                : 'No artwork has been shared yet. Be the first to share your creativity!'
                            }
                        </p>
                    </div>
                )}

                {/* Gallery Grid */}
                {!loading && !error && images.length > 0 && (
                    <div className={
                        viewMode === 'grid'
                            ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6"
                            : "space-y-3 sm:space-y-4"
                    }>
                        {images.map((image) => (
                            <div 
                                key={image.id}
                                className={
                                    viewMode === 'grid'
                                        ? "glass-card overflow-hidden hover:scale-105 transition-all duration-300 group"
                                        : "glass-card p-4 flex gap-4 hover:bg-gray-800/50 transition-all duration-300 group"
                                }
                            >
                                {viewMode === 'grid' ? (
                                    <>
                                        {/* Grid View */}
                                        <div className="relative aspect-square bg-gray-900 overflow-hidden">
                                            <img
                                                src={image.optimized_url || image.file_url}
                                                alt={image.title || image.original_filename}
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            <AccessLevelBadge accessLevel={image.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />

                                            {isGated(image.access_level, isAuthenticated, hasActiveSubscription) ? (
                                                /* Protected: always-visible gradient overlay */
                                                <Link
                                                    to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                                    className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3"
                                                    onClick={e => e.stopPropagation()}
                                                >
                                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(image.access_level, 'view')}</span>
                                                </Link>
                                            ) : (
                                                /* Public: hover overlay with action buttons */
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleLike(image)}
                                                            className={`p-2 rounded-lg transition-colors ${
                                                                image.user_has_liked
                                                                    ? 'bg-red-500/80 hover:bg-red-600'
                                                                    : 'bg-gray-500/80 hover:bg-red-500/80'
                                                            }`}
                                                            title={
                                                                isAuthenticated
                                                                    ? (image.user_has_liked ? 'Liked' : 'Like')
                                                                    : 'Sign up to like images'
                                                            }
                                                        >
                                                            <Heart className={`w-4 h-4 text-white ${image.user_has_liked ? 'fill-current' : ''}`} />
                                                        </button>
                                                        <Link
                                                            to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                                            className="p-2 bg-blue-500/80 rounded-lg hover:bg-blue-600 transition-colors"
                                                            title="View Full Size"
                                                        >
                                                            <Eye className="w-4 h-4 text-white" />
                                                        </Link>
                                                        <button
                                                            onClick={() => handleShareClick(image)}
                                                            className="p-2 bg-purple-500/80 rounded-lg hover:bg-purple-600 transition-colors"
                                                            title="Share"
                                                        >
                                                            <Share2 className="w-4 h-4 text-white" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDownloadClick(image)}
                                                            className="p-2 bg-green-500/80 rounded-lg hover:bg-green-600 transition-colors"
                                                            title="Download"
                                                        >
                                                            <Download className="w-4 h-4 text-white" />
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* File Size Badge */}
                                            <div className="absolute top-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                                {formatFileSize(image.file_size)}
                                            </div>
                                        </div>
                                        
                                        {/* Image Info */}
                                        <div className="p-4">
                                            <h3 className="font-semibold text-white text-sm mb-1 truncate">
                                                {image.title || image.original_filename}
                                            </h3>
                                            <div className="flex items-center gap-1 text-xs text-gray-400 mb-2">
                                                <span>🌊</span>
                                                <Link 
                                                    to={`/profile/${image.username}`}
                                                    className="hover:text-blue-400 transition-colors truncate"
                                                >
                                                    {image.artist || image.display_name || image.username}
                                                </Link>
                                            </div>
                                            <div className="flex items-center justify-between text-xs text-gray-500">
                                                <span>{image.medium || 'Digital'}</span>
                                                <div className="flex items-center gap-1">
                                                    <Eye className="w-3 h-3" />
                                                    <span>{(image.view_count || image.play_count || 0).toLocaleString()}</span>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        {/* List View */}
                                        <div className="relative w-20 h-20 bg-gray-900 rounded-lg overflow-hidden flex-shrink-0">
                                            <img
                                                src={image.optimized_url || image.file_url}
                                                alt={image.title || image.original_filename}
                                                width="80"
                                                height="80"
                                                className="w-full h-full object-cover"
                                                loading="lazy"
                                            />
                                            <AccessLevelBadge accessLevel={image.access_level} iconOnly isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                        </div>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-start justify-between">
                                                <div className="flex-1 min-w-0">
                                                    <h3 className="font-semibold text-white mb-1 truncate">
                                                        {image.title || image.original_filename}
                                                    </h3>
                                                    <div className="flex items-center gap-2 text-sm text-gray-400 mb-2">
                                                        <Link 
                                                            to={`/profile/${image.username}`}
                                                            className="hover:text-blue-400 transition-colors"
                                                        >
                                                            {image.artist || image.display_name || image.username}
                                                        </Link>
                                                        <span>•</span>
                                                        <span>{formatTimeAgo(image.created_at)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-4 text-xs text-gray-500">
                                                        <span>{image.medium || 'Digital'}</span>
                                                        <span>{image.category || 'Artwork'}</span>
                                                        <span>{formatFileSize(image.file_size)}</span>
                                                    </div>
                                                </div>
                                                
                                                <div className="flex items-center gap-1 ml-4">
                                                    <button
                                                        onClick={() => handleLike(image)}
                                                        className={`p-2 transition-colors ${
                                                            image.user_has_liked
                                                                ? 'text-red-400 hover:text-red-300' 
                                                                : 'text-gray-400 hover:text-red-400'
                                                        }`}
                                                        title={
                                                            isAuthenticated 
                                                                ? (image.user_has_liked ? 'Liked' : 'Like') 
                                                                : 'Sign up to like images'
                                                        }
                                                    >
                                                        <Heart className={`w-4 h-4 ${image.user_has_liked ? 'fill-current' : ''}`} />
                                                    </button>
                                                    <Link
                                                        to={`/view/image/${image.username}/${image.stored_filename || image.original_filename || image.id}`}
                                                        className="p-2 text-gray-400 hover:text-blue-400 transition-colors"
                                                        title="View"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </Link>
                                                    <button
                                                        onClick={() => handleShareClick(image)}
                                                        className="p-2 text-gray-400 hover:text-purple-400 transition-colors"
                                                        title="Share"
                                                    >
                                                        <Share2 className="w-4 h-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDownloadClick(image)}
                                                        className="p-2 text-gray-400 hover:text-green-400 transition-colors"
                                                        title="Download"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </button>
                                                    <div className="flex items-center gap-1 text-xs text-gray-500 ml-2">
                                                        <Eye className="w-3 h-3" />
                                                        <span>{(image.view_count || image.play_count || 0).toLocaleString()}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, image: null })}
                mediaTitle={shareModal.image?.title || shareModal.image?.original_filename || 'Unknown Artwork'}
                mediaType="image"
                username={shareModal.image?.username}
                mediaId={shareModal.image?.stored_filename || shareModal.image?.original_filename || shareModal.image?.id}
                artistName={shareModal.image?.display_name || shareModal.image?.username}
            />

            {/* Download Paywall Modal */}
            <DownloadPaywall
                isOpen={downloadPaywall.isOpen}
                onClose={() => setDownloadPaywall({ isOpen: false, image: null })}
                mediaTitle={downloadPaywall.image?.title || downloadPaywall.image?.original_filename || 'Unknown Artwork'}
                mediaType="image"
                uploadId={downloadPaywall.image?.id}
            />
            
            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'image' })}
                contentType={signupInvite.contentType}
            />
        </div>
    );
};

export default Gallery;